import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Typography,
  Alert,
  Divider,
  Paper,
  CircularProgress,
} from '@mui/material';
import { Save as SaveIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import api from '../../services/api';

const REPO_FIELDS = [
  'subdominio', 'dominio_proprio', 'repositorio_activo',
  'repositorio_titulo', 'repositorio_slogan', 'repositorio_logo',
  'repositorio_hero_imagem', 'repositorio_cor_primaria', 'repositorio_cor_secundaria',
];

const ConfiguracaoRepositorio = ({ instituicaoId, instituicao }) => {
  const [form, setForm] = useState({
    subdominio: '',
    dominio_proprio: '',
    repositorio_activo: false,
    repositorio_titulo: '',
    repositorio_slogan: '',
    repositorio_logo: '',
    repositorio_hero_imagem: '',
    repositorio_cor_primaria: '#1565C0',
    repositorio_cor_secundaria: '#0D47A1',
  });
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    if (instituicao) {
      const initial = {};
      REPO_FIELDS.forEach((f) => {
        initial[f] = instituicao[f] ?? (f === 'repositorio_activo' ? false :
          f === 'repositorio_cor_primaria' ? '#1565C0' :
          f === 'repositorio_cor_secundaria' ? '#0D47A1' : '');
      });
      setForm(initial);
    }
  }, [instituicao]);

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      await api.patch(`/instituicoes/${instituicaoId}/repositorio-config`, form);
      setFeedback({ type: 'success', msg: 'Configuração guardada com sucesso.' });
    } catch (err) {
      setFeedback({ type: 'error', msg: err.response?.data?.message || 'Erro ao guardar.' });
    } finally {
      setSaving(false);
    }
  };

  const repoUrl = form.subdominio
    ? `https://${form.subdominio}.zenkroll.com/repositorio`
    : null;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Repositório Público
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure o repositório digital público da sua instituição. Os documentos arquivados
        ficarão acessíveis no endereço abaixo.
      </Typography>

      {feedback && (
        <Alert severity={feedback.type} sx={{ mb: 2 }} onClose={() => setFeedback(null)}>
          {feedback.msg}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom color="primary">
          Activação e Endereço
        </Typography>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!form.repositorio_activo}
                  onChange={handleChange('repositorio_activo')}
                  color="primary"
                />
              }
              label="Repositório activo (visível publicamente)"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Subdomínio"
              placeholder="minhaescola"
              value={form.subdominio}
              onChange={handleChange('subdominio')}
              helperText="O repositório ficará acessível em subdominio.zenkroll.com"
              InputProps={{
                endAdornment: form.subdominio ? (
                  <Typography variant="caption" color="text.secondary">.zenkroll.com</Typography>
                ) : null,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Domínio próprio (opcional)"
              placeholder="repositorio.minhaescola.mz"
              value={form.dominio_proprio}
              onChange={handleChange('dominio_proprio')}
              helperText="Configure um CNAME apontando para zenkroll.com"
            />
          </Grid>

          {repoUrl && (
            <Grid item xs={12}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<OpenInNewIcon />}
                href={repoUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Abrir repositório: {repoUrl}
              </Button>
            </Grid>
          )}
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom color="primary">
          Identidade Visual
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Título do repositório"
              placeholder="Repositório Digital"
              value={form.repositorio_titulo}
              onChange={handleChange('repositorio_titulo')}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Slogan / Descrição curta"
              placeholder="Aceda ao conhecimento académico da instituição"
              value={form.repositorio_slogan}
              onChange={handleChange('repositorio_slogan')}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="URL do logótipo"
              placeholder="https://..."
              value={form.repositorio_logo}
              onChange={handleChange('repositorio_logo')}
              helperText="URL público para a imagem do logótipo (PNG/SVG recomendado)"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="URL da imagem de destaque (hero)"
              placeholder="https://..."
              value={form.repositorio_hero_imagem}
              onChange={handleChange('repositorio_hero_imagem')}
              helperText="Imagem de fundo do cabeçalho (opcional)"
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Cor primária"
              type="color"
              value={form.repositorio_cor_primaria}
              onChange={handleChange('repositorio_cor_primaria')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Cor secundária"
              type="color"
              value={form.repositorio_cor_secundaria}
              onChange={handleChange('repositorio_cor_secundaria')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Preview */}
          <Grid item xs={12} sm={6}>
            <Box
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                height: 80,
                background: `linear-gradient(135deg, ${form.repositorio_cor_secundaria} 0%, ${form.repositorio_cor_primaria} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="body2" color="white" fontWeight={600}>
                Pré-visualização do gradiente
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Divider sx={{ mb: 2 }} />

      <Button
        variant="contained"
        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
        onClick={handleSave}
        disabled={saving}
      >
        Guardar Configuração
      </Button>
    </Box>
  );
};

ConfiguracaoRepositorio.propTypes = {
  instituicaoId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  instituicao: PropTypes.object,
};

export default ConfiguracaoRepositorio;
